import { pick } from 'lodash';

import { useAssistantCloudStore } from './stores/assistant-cloud-store';
import { ensureStoreInitialized } from './utils';

import type {
  MessageData,
  MessageCreateInput,
  MessageUpdateInput,
  MessageListOptions,
  MessageListResponse,
  MessageCreateResponse,
} from './types';

const MESSAGE_FIELDS = [
  'id',
  'parent_id',
  'height',
  'format',
  'content',
  'created_at',
  'updated_at',
] as const;

export class MessagesAPI {
  async create(threadId: string, data: MessageCreateInput): Promise<MessageCreateResponse> {
    await ensureStoreInitialized();
    const { createMessage } = useAssistantCloudStore.getState();
    const messageId = await createMessage(threadId, data);
    return { message_id: messageId };
  }

  /**
   * Update an existing message's content. Bumps `updated_at` to `new Date()`
   * — the upstream `assistant-cloud` library calls this on every content
   * update during streaming, so for a fully-streamed message
   * `updated_at - created_at` is the persisted stream duration.
   */
  async update(threadId: string, messageId: string, data: MessageUpdateInput): Promise<void> {
    await ensureStoreInitialized();
    const { updateMessage } = useAssistantCloudStore.getState();
    await updateMessage(threadId, messageId, data);
  }

  async list(threadId: string, options?: MessageListOptions): Promise<MessageListResponse> {
    await ensureStoreInitialized();
    const { listMessages } = useAssistantCloudStore.getState();
    const messages = await listMessages(threadId);
    // Collision-free by construction (createMessage replaces dup ids; DB loads run
    // filterValidMessageChain), so the import converter never hits the "same id" crash.
    const targetFormat = options?.format;
    return {
      messages: [...messages].reverse().map(m => {
        const picked = pick(m, MESSAGE_FIELDS);
        // Normalize format for old messages stored before an ai-sdk format version upgrade.
        // Without this, assistant-ui's FormattedCloudPersistence filters them out since it
        // expects all messages to match the current format (e.g., "ai-sdk/v6").
        if (targetFormat && picked.format !== targetFormat) {
          picked.format = targetFormat;
        }
        return picked;
      }),
    };
  }

  async get(messageId: string): Promise<MessageData | null> {
    await ensureStoreInitialized();
    const { getMessage } = useAssistantCloudStore.getState();
    const message = getMessage(messageId);
    return message ? pick(message, MESSAGE_FIELDS) : null;
  }
}
